const BANNER_SELECTOR = '[data-campaign-banner]';
const DISMISSED_CLASS = 'is-dismissed';
const BODY_CLASS = 'has-campaign-banner';
const OFFSET_VAR = '--sts-campaign-banner-offset';
const CONTAINER_ATTR = 'data-campaign-banner-container';
const INITIALIZED_ATTR = 'bannerInitialized';
const MOUNTED_ATTR = 'bannerMounted';
const OBSERVED_ATTR = 'bannerResizeObserved';
const STORAGE_KEY = 'stsCampaignBannerDismissalsV1';
const GLOBAL_OWNER_PREFIX = 'global-';
const DEFAULT_DISMISS_DURATION_HOURS = 24;

type DismissalMap = Record<string, number>;
let localStorageAvailable: boolean | null = null;

const canUseLocalStorage = (): boolean => {
    if (localStorageAvailable !== null) {
        return localStorageAvailable;
    }

    if (typeof window === 'undefined') {
        localStorageAvailable = false;
        return false;
    }

    try {
        const probeKey = '__sts_campaign_banner_probe__';
        window.localStorage.setItem(probeKey, '1');
        window.localStorage.removeItem(probeKey);
        localStorageAvailable = true;
        return true;
    } catch {
        localStorageAvailable = false;
        return false;
    }
};

const readDismissalMap = (): DismissalMap => {
    if (!canUseLocalStorage()) {
        return {};
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') {
            return {};
        }

        const normalized: DismissalMap = {};
        Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                normalized[key] = value;
            }
        });

        return normalized;
    } catch {
        return {};
    }
};

const writeDismissalMap = (value: DismissalMap) => {
    if (!canUseLocalStorage()) {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
        // Ignore quota/security errors.
    }
};

const pruneExpiredDismissals = (value: DismissalMap): { map: DismissalMap; changed: boolean } => {
    const now = Date.now();
    let changed = false;
    const next: DismissalMap = { ...value };

    Object.entries(next).forEach(([key, expiresAt]) => {
        if (!Number.isFinite(expiresAt) || expiresAt <= now) {
            delete next[key];
            changed = true;
        }
    });

    return { map: next, changed };
};

const init = () => {
    if (typeof document === 'undefined') {
        return;
    }

    if (document.body?.classList.contains('block-editor-page')) {
        return;
    }

    const ensureContainer = () => {
        let container = document.querySelector<HTMLElement>(`[${CONTAINER_ATTR}]`);
        if (container) {
            return container;
        }

        container = document.createElement('div');
        container.setAttribute(CONTAINER_ATTR, 'true');

        const header = document.querySelector<HTMLElement>('.site-header');
        const parent = header?.parentElement || document.body;

        if (header) {
            parent?.insertBefore(container, header);
        } else if (parent) {
            parent.insertBefore(container, parent.firstChild);
        } else {
            document.body.appendChild(container);
        }

        return container;
    };

    const setOffsetVar = (value?: string) => {
        if (!value) {
            document.documentElement?.style.removeProperty(OFFSET_VAR);
            document.body?.style.removeProperty(OFFSET_VAR);
            return;
        }
        document.documentElement?.style.setProperty(OFFSET_VAR, value);
        document.body?.style.setProperty(OFFSET_VAR, value);
    };

    const updateBodyOffset = () => {
        const container = document.querySelector<HTMLElement>(`[${CONTAINER_ATTR}]`);
        if (!container) {
            document.body?.classList.remove(BODY_CLASS);
            setOffsetVar();
            return;
        }

        const height = container.offsetHeight;
        if (!height) {
            document.body?.classList.remove(BODY_CLASS);
            setOffsetVar();
            return;
        }

        setOffsetVar(`${height}px`);
        document.body?.classList.add(BODY_CLASS);
    };

    const getDismissDurationHours = (banner: HTMLElement): number => {
        const raw = banner.dataset.dismissHours;
        const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;

        if (!Number.isFinite(parsed)) {
            return DEFAULT_DISMISS_DURATION_HOURS;
        }

        return Math.max(0, parsed);
    };

    const getOwnerKey = (banner: HTMLElement): string => {
        const explicitOwner = (banner.dataset.campaignBannerOwner || '').trim();
        if (explicitOwner !== '') {
            return explicitOwner;
        }

        const classList = Array.from(document.body?.classList || []);
        const postIdClass = classList.find((className) => /^postid-\d+$/.test(className));
        if (postIdClass) {
            const postId = postIdClass.replace('postid-', '');
            return `post-${postId}`;
        }

        return `path-${window.location.pathname}`;
    };

    const getDismissKey = (banner: HTMLElement): string => {
        const explicitKey = (banner.dataset.dismissKey || '').trim();
        if (explicitKey !== '') {
            return explicitKey;
        }

        const text = banner.querySelector<HTMLElement>('.sts-block-campaign-banner__text-visual')?.textContent || '';
        const normalized = text.trim().replace(/\s+/g, ' ');

        if (normalized !== '') {
            return normalized.slice(0, 120);
        }

        return 'default';
    };

    const getStorageEntryKey = (banner: HTMLElement): string => {
        const ownerKey = getOwnerKey(banner);

        if (ownerKey.startsWith(GLOBAL_OWNER_PREFIX)) {
            return ownerKey;
        }

        return `${ownerKey}::${getDismissKey(banner)}`;
    };

    const isDismissedFromStorage = (banner: HTMLElement): boolean => {
        const key = getStorageEntryKey(banner);
        if (!key) {
            return false;
        }

        const { map, changed } = pruneExpiredDismissals(readDismissalMap());
        const expiresAt = map[key];

        if (changed) {
            writeDismissalMap(map);
        }

        return typeof expiresAt === 'number' && Number.isFinite(expiresAt) && expiresAt > Date.now();
    };

    const persistDismissal = (banner: HTMLElement) => {
        const hours = getDismissDurationHours(banner);
        if (hours <= 0) {
            return;
        }

        const key = getStorageEntryKey(banner);
        if (!key) {
            return;
        }

        const { map } = pruneExpiredDismissals(readDismissalMap());
        map[key] = Date.now() + hours * 60 * 60 * 1000;
        writeDismissalMap(map);
    };

    const dismissBanner = (banner: HTMLElement, persist = true) => {
        banner.classList.add(DISMISSED_CLASS);
        banner.setAttribute('aria-hidden', 'true');

        if (persist) {
            persistDismissal(banner);
        }

        updateBodyOffset();
    };

    const mountBanner = (banner: HTMLElement): boolean => {
        if (banner.dataset[MOUNTED_ATTR] === 'true') {
            return false;
        }

        if (banner.closest(`[${CONTAINER_ATTR}]`)) {
            banner.dataset[MOUNTED_ATTR] = 'true';
            return false;
        }

        const container = ensureContainer();
        container.appendChild(banner);
        banner.dataset[MOUNTED_ATTR] = 'true';
        return true;
    };

    const observeBannerResize = (banner: HTMLElement) => {
        if (banner.dataset[OBSERVED_ATTR] === 'true') {
            return;
        }
        banner.dataset[OBSERVED_ATTR] = 'true';

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(() => updateBodyOffset());
            observer.observe(banner);
        }
    };

    const initBanner = (banner: HTMLElement): boolean => {
        const wasMountedNow = mountBanner(banner);

        if (banner.dataset[INITIALIZED_ATTR] !== 'true') {
            banner.dataset[INITIALIZED_ATTR] = 'true';

            const dismissButton = banner.querySelector<HTMLButtonElement>('[data-dismiss-button]');
            if (dismissButton) {
                dismissButton.addEventListener('click', () => dismissBanner(banner));
            }
        }

        if (isDismissedFromStorage(banner)) {
            dismissBanner(banner, false);
        }

        observeBannerResize(banner);
        return wasMountedNow;
    };

    const processBanners = (root: ParentNode = document) => {
        let hasMountedAny = false;
        const banners = Array.from(root.querySelectorAll<HTMLElement>(BANNER_SELECTOR));

        banners.forEach((banner) => {
            if (initBanner(banner)) {
                hasMountedAny = true;
            }
        });

        if (hasMountedAny) {
            updateBodyOffset();
        }
    };

    const processNode = (node: Node) => {
        if (!(node instanceof HTMLElement)) {
            return;
        }

        if (node.matches(BANNER_SELECTOR)) {
            const hasMounted = initBanner(node);
            if (hasMounted) {
                updateBodyOffset();
            }
            return;
        }

        processBanners(node);
    };

    processBanners(document);

    const observer = new MutationObserver((mutations) => {
        let shouldRecalculateOffset = false;

        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    processNode(node);
                    if (node.matches(BANNER_SELECTOR) || node.querySelector(BANNER_SELECTOR)) {
                        shouldRecalculateOffset = true;
                    }
                }
            });

            if (mutation.removedNodes.length) {
                shouldRecalculateOffset = true;
            }
        });

        if (shouldRecalculateOffset) {
            updateBodyOffset();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });

    updateBodyOffset();
    window.addEventListener('resize', updateBodyOffset);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
